/* @flow */

import classNames from 'classnames';
import { compose } from 'ramda';
import * as React from 'react';
import { createUseStyles } from 'react-jss';

import styles from './TakeOver.style';
import {
  enableBodyScrolling,
  disableBodyScrolling,
} from 'src/shared/core/util/browserUtil';
import CloseButton from 'src/shared/ui/component/button/CloseButton';

type Props = {
  bypassOpeningAnimation?: boolean,
  hideCloseButton?: boolean,
  forceClosing?: boolean,
  handleOpen?: () => void,
  handleDismiss?: () => void,
  children: React.Node,
};

const useStyles = createUseStyles(styles);

const TakeOver = ({
  bypassOpeningAnimation,
  hideCloseButton,
  forceClosing,
  children,
  handleOpen,
  handleDismiss,
}: Props): React.Node => {
  /*
   |----------------------------------------------------------------------------
   | State
   |----------------------------------------------------------------------------
   */

  const [isHidden, setIsHidden] = React.useState(!bypassOpeningAnimation);

  /*
   |----------------------------------------------------------------------------
   | Effects
   |----------------------------------------------------------------------------
   */

  React.useEffect(() => {
    if (!bypassOpeningAnimation) {
      open();
    }

    disableBodyScrolling();

    return () => {
      enableBodyScrolling();
    };
  }, []);

  React.useEffect(() => {
    if (forceClosing) {
      close();
    }
  }, [forceClosing]);

  /*
   |----------------------------------------------------------------------------
   | Event Handlers
   |----------------------------------------------------------------------------
   */

  const onDismiss = () => close();

  const onTransitionEnd = ({ target }: TransitionEvent) => {
    const { current: rootElt } = rootRef;

    if (target === rootElt) {
      if (isHidden) {
        if (handleDismiss) {
          handleDismiss();
        }
      } else if (handleOpen) {
        handleOpen();
      }
    }
  };

  /*
   |----------------------------------------------------------------------------
   | Functions
   |----------------------------------------------------------------------------
   */

  const open = () => {
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setIsHidden(false)),
    );
  };

  const close = () => {
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setIsHidden(true)),
    );
  };

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  const rootClasses = classNames(classes.root, {
    [classes['root-isHidden']]: isHidden,
  });

  /*
   |----------------------------------------------------------------------------
   | Refs
   |----------------------------------------------------------------------------
   */

  const rootRef = React.createRef();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const closeBtn = !hideCloseButton && (
    <CloseButton layoutClass={classes.closeBtn} handleClick={onDismiss} />
  );

  return (
    <div
      className={rootClasses}
      onTransitionEnd={onTransitionEnd}
      ref={rootRef}
    >
      {closeBtn}
      {children}
    </div>
  );
};

export default compose(React.memo)(TakeOver);
